/** Pieces the four dashboards share: the search that travels with a drill-through link, a list of
 * bars with a text alternative, and the words for a count a role is not asked.
 *
 * **Every mark is a link or a button to the list behind it** (ADR-0195: *any number opens the same
 * receipt drawer or the same list the other screens open*), and every list of bars carries the same
 * rows as a table for a screen reader (ADR-0090's first rule).
 */

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { ChartTable } from "../../components/charts/ChartTable";
import type { ScreenSearch } from "../search";
import styles from "./Dashboards.module.css";

export type Destination =
  | "/emissions"
  | "/sources"
  | "/evidence"
  | "/reports"
  | "/readiness"
  | "/tasks"
  | "/approvals"
  | "/suppliers"
  | "/transition"
  | "/risks"
  | "/ledger";

export type Fill = "scope1" | "scope2" | "scope3" | "info" | "warn" | "muted" | "ink";

const FILL_CLASS: Record<Fill, string> = {
  scope1: styles.fillScope1 ?? "",
  scope2: styles.fillScope2 ?? "",
  scope3: styles.fillScope3 ?? "",
  info: styles.fillInfo ?? "",
  warn: styles.fillWarn ?? "",
  muted: styles.fillMuted ?? "",
  ink: "",
};

export interface BarRow {
  id: string;
  name: string;
  sub?: string;
  /** 0 to 1, geometry only. */
  fraction: number;
  /** The figure as printed (`12`, `1,686.5 t`). */
  figure: string;
  fill?: Fill;
  /** The bar as parts of one whole, each in its scope's colour (tonnes take their scope's colour).
   * `weight` is geometry only: a part's width against the others'. Without it the bar is one fill. */
  segments?: readonly { fill: Fill; weight: number }[];
  /** `outline` is a recorded absence ("Not assessed"): a dashed track, never a short bar. */
  state?: "drawn" | "outline";
  /** Where the mark opens. A row with none is static. */
  to?: { to: Destination; search: ScreenSearch };
  /** What the link does, for the accessible name. */
  openLabel?: string;
}

export function BarRows({
  rows,
  caption,
  head,
  testId,
}: {
  rows: readonly BarRow[];
  caption: string;
  /** The table's column heads, name first, figure last. */
  head: readonly [string, string];
  testId: string;
}) {
  return (
    <div data-testid={testId}>
      <ul className={styles.rows}>
        {rows.map((row) => {
          const content: ReactNode = (
            <>
              <span className={styles.name}>
                {row.name}
                {row.sub ? <span className={styles.sub}>{row.sub}</span> : null}
              </span>
              {row.state === "outline" ? (
                <span className={styles.outline} aria-hidden="true" />
              ) : (
                <span className={styles.track} aria-hidden="true">
                  {row.segments && row.segments.length > 0 ? (
                    <span
                      className={styles.stack}
                      style={{ width: `${Math.max(0, Math.min(1, row.fraction)) * 100}%` }}
                    >
                      {row.segments.map((part) => (
                        <span
                          key={part.fill}
                          className={`${styles.part} ${FILL_CLASS[part.fill]}`}
                          data-part={part.fill}
                          style={{ flexGrow: Math.max(0, part.weight) }}
                        />
                      ))}
                    </span>
                  ) : (
                    <span
                      className={`${styles.fill} ${FILL_CLASS[row.fill ?? "ink"]}`}
                      style={{ width: `${Math.max(0, Math.min(1, row.fraction)) * 100}%` }}
                    />
                  )}
                </span>
              )}
              <span className={styles.figure}>{row.figure}</span>
            </>
          );
          return (
            <li key={row.id} data-mark={row.id}>
              {row.to ? (
                <Link
                  to={row.to.to}
                  search={row.to.search}
                  className={`${styles.row} ${styles.rowLink}`}
                  aria-label={row.openLabel ?? `${row.name}: ${row.figure}. Open the list behind it.`}
                  data-testid="dash-mark"
                >
                  {content}
                </Link>
              ) : (
                <div className={styles.row} data-testid="dash-mark">
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <ChartTable
        caption={caption}
        head={head}
        rows={rows.map((row) => [row.name + (row.sub ? `, ${row.sub}` : ""), row.figure])}
      />
    </div>
  );
}

/** A count a role is not asked is `null` on the wire; it is drawn in words, never `0`. */
export function CountText({ value }: { value: number | null | undefined }) {
  return value === null || value === undefined ? (
    <span className={styles.notShown}>not shown to your role</span>
  ) : (
    <>{value}</>
  );
}
